/**
 * Pure helpers lifted out of `MqttSourceLegacy.ts`.
 *
 * Everything here is stateless and touches no broker connection, so it is
 * testable on its own. The functions were extracted verbatim — behaviour is
 * unchanged; see `mqttHelpers.test.ts` for the pinned edge cases.
 *
 * Two groups live here:
 *
 *  - **Connection + envelope plumbing** — `reasonCodeText`, `mergeNonNull`.
 *  - **Target fusion** — `emitterRank`, `hasHigherTrust`, `pickEmitter` and
 *    their ranking tables, used together by `emitFusedTargets`.
 */
import type { PartialTarget } from './legacy/surveillanceTraffic';

/** Human label for the MQTT CONNACK reason codes mqtt.js puts on `error.code`,
 *  so a connection failure names its actual cause instead of being silent.
 *  Covers both MQTT 3.1.1 return codes (1–5) and MQTT 5 reason codes (0x80+). */
export function reasonCodeText(code: unknown): string {
  switch (code) {
    case 1:
      return 'Unacceptable protocol version';
    case 2:
      return 'Client identifier rejected';
    case 3:
    case 136:
      return 'Server unavailable';
    case 4:
    case 134:
      return 'Bad username or password';
    case 5:
    case 135:
      return 'Not authorized';
    case 138:
      return 'Banned';
    case 159:
      return 'Connection rate exceeded';
    default:
      return 'see message';
  }
}

/** Merge `update` into `existing`, treating null/undefined as "no change".
 *  Lets a partial MAVLink update fill in battery without wiping the
 *  lat/lng a previous GLOBAL_POSITION_INT message just wrote. */
export function mergeNonNull<T extends Record<string, unknown>>(
  existing: T,
  update: Partial<T>,
): T {
  const out = { ...existing };
  for (const [k, v] of Object.entries(update)) {
    if (v !== null && v !== undefined) {
      (out as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}

/* --------------------------------------------------------------------------
 * Target fusion
 *
 * Several surveillance emitters report the same aircraft at once and
 * disagree about its type, speed units and altitude precision. The helpers
 * below decide whose version the map draws.
 *
 * The model is *sticky*: a winner keeps winning until it goes stale or a
 * strictly-higher-trust emitter shows up. That stickiness is the point —
 * without it, two equal-trust emitters that disagree make the marker flip
 * on every 1 Hz emit cycle.
 *
 * @see MqttSourceLegacy.emitFusedTargets
 * ----------------------------------------------------------------------- */

/** One emitter's view of a target, plus when we last heard it. Mirrors the
 *  slot shape `MqttSourceLegacy` keeps in `fusedTargets`. */
export interface EmitterSlot {
  partial: PartialTarget;
  seenAt: number;
}

/** Trust-level priority for surviving-emitter selection. Higher = better. */
export const TRUST_RANK: Record<PartialTarget['trust'], number> = {
  high: 3,
  medium: 2,
  low: 1,
};

/** Stable preference order for choosing a winner when multiple emitters
 *  share the same trust tier. Order matches the broker emitters listed
 *  in ticket 86e1fvf06: cooperative ADS-B first (cleanest data), then
 *  MLAT, then Vision (sanitisation pending — vision sometimes ships gs
 *  in m/s and altitude with metre precision; deprioritise until that's
 *  cleaned up so cleaner sources win for shared aircraft), then
 *  drone_rf, then the three regional aggregators. Anything else (would
 *  be a broker quirk) lands at the end. */
export const EMITTER_PRIORITY: readonly string[] = [
  'live_ads',
  'live_mlat',
  'live_vision',
  'live_drone_rf',
  'live_regional_edmonton',
  'live_regional_calgary',
  'live_regional_fort_mcmurray',
];

export function emitterRank(emitter: string): number {
  const i = EMITTER_PRIORITY.indexOf(emitter);
  if (i >= 0) return i;
  if (emitter.startsWith('live_regional_'))
    return EMITTER_PRIORITY.indexOf('live_regional_edmonton');
  return EMITTER_PRIORITY.length;
}

export function hasHigherTrust(
  slots: Map<string, EmitterSlot>,
  current: PartialTarget['trust'],
): boolean {
  const curRank = TRUST_RANK[current];
  for (const slot of slots.values()) {
    if (TRUST_RANK[slot.partial.trust] > curRank) return true;
  }
  return false;
}

export function pickEmitter(slots: Map<string, EmitterSlot>): string | null {
  let best: string | null = null;
  let bestTrust = -1;
  let bestPriority = Number.POSITIVE_INFINITY;
  for (const [emitter, slot] of slots) {
    const trust = TRUST_RANK[slot.partial.trust];
    const priority = emitterRank(emitter);
    if (trust > bestTrust || (trust === bestTrust && priority < bestPriority)) {
      best = emitter;
      bestTrust = trust;
      bestPriority = priority;
    }
  }
  return best;
}
